import { useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PageHeader } from "../components/AppLayout";
import { StatusBadge } from "../components/StatusBadge";
import { CheckCircle2, Circle, Clock, User, Home, Briefcase, Bell } from "lucide-react";
import { toast } from "sonner";
import { VISITAS, agentePorId } from "../data/mockData";
import { fechaCorta } from "../lib/format";

function ConfirmRow({ ok, label, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      ) : (
        <Circle className="h-4 w-4 text-slate-300" />
      )}
      <Icon className="h-3.5 w-3.5 text-slate-400" />
      <span className={ok ? "text-slate-700" : "text-slate-400"}>{label}</span>
    </div>
  );
}

export default function Visitas() {
  const [visitas, setVisitas] = useState(VISITAS);
  const [tab, setTab] = useState("todas");

  const elegirFranja = (visitaId, idx) => {
    setVisitas((vs) =>
      vs.map((v) =>
        v.id === visitaId
          ? {
              ...v,
              franjasPropuestas: v.franjasPropuestas.map((f, i) => ({ ...f, elegida: i === idx })),
              estado: "Confirmada",
              confirmaciones: { interesado: true, inquilino: true, agente: true },
              recordatorioEnviado: true,
            }
          : v
      )
    );
    toast.success("Franja elegida. Visita confirmada a los tres: interesado, inquilino y agente. Recordatorios enviados.");
  };

  const confirmarTres = (visitaId) => {
    setVisitas((vs) =>
      vs.map((v) =>
        v.id === visitaId
          ? { ...v, estado: "Confirmada", confirmaciones: { interesado: true, inquilino: true, agente: true }, recordatorioEnviado: true }
          : v
      )
    );
    toast.success("Confirmada a los tres. Salen los recordatorios por WhatsApp.");
  };

  const ocupados = visitas.filter((v) => v.tipo === "Depto ocupado");
  const libres = visitas.filter((v) => v.tipo === "Depto libre");
  const lista = tab === "ocupados" ? ocupados : tab === "libres" ? libres : visitas;

  return (
    <div>
      <PageHeader
        titulo="Coordinación de visitas"
        descripcion="Para departamentos ocupados, el inquilino marca franjas y se confirma a los tres. Para los libres, el interesado agenda directo según la agenda del agente."
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList className="rounded-md">
          <TabsTrigger value="todas" data-testid="visits-tab-all" className="rounded-md">Todas ({visitas.length})</TabsTrigger>
          <TabsTrigger value="ocupados" data-testid="visits-tab-occupied" className="rounded-md">Ocupados ({ocupados.length})</TabsTrigger>
          <TabsTrigger value="libres" data-testid="visits-tab-free" className="rounded-md">Libres ({libres.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {lista.map((v) => {
          const ag = agentePorId(v.agenteId);
          return (
            <Card key={v.id} data-testid={`visit-card-${v.id}`} className="rounded-lg border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{v.direccion}</p>
                  <p className="text-xs text-slate-500">{v.barrio} · {v.propiedadTitulo}</p>
                </div>
                <StatusBadge estado={v.estado} testId={`visit-item-status-${v.id}`} />
              </div>
              <div className="px-5 py-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Interesado</p>
                  <p className="text-sm text-slate-800">{v.interesado.nombre}</p>
                  <p className="text-xs text-slate-500">{v.interesado.telefono}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1">Fecha y agente</p>
                  <p className="text-sm text-slate-800 flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-slate-400" /> {fechaCorta(v.fecha)} · {v.hora} hs</p>
                  <p className="text-xs text-slate-500">{ag?.nombre}</p>
                </div>
              </div>

              {v.tipo === "Depto ocupado" && v.franjasPropuestas.length > 0 && (
                <div className="px-5 pb-3">
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-2">
                    Franjas marcadas por el inquilino
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {v.franjasPropuestas.map((f, i) => (
                      <button
                        key={i}
                        onClick={() => v.estado === "Propuesta" && elegirFranja(v.id, i)}
                        data-testid={`visit-slot-${v.id}-${i}`}
                        className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                          f.elegida
                            ? "border-brand bg-brand-light text-brand"
                            : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        }`}
                      >
                        {f.dia} · {f.rango}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
                <div className="space-y-1">
                  <ConfirmRow ok={v.confirmaciones.interesado} label="Interesado" icon={User} />
                  <ConfirmRow ok={v.confirmaciones.inquilino} label="Inquilino" icon={Home} />
                  <ConfirmRow ok={v.confirmaciones.agente} label="Agente" icon={Briefcase} />
                </div>
                <div className="flex flex-col items-end gap-2">
                  {v.recordatorioEnviado && (
                    <Badge variant="outline" className="rounded-md bg-blue-50 text-blue-800 border-blue-200 text-[11px]">
                      <Bell className="mr-1 h-3 w-3" /> Recordatorio enviado
                    </Badge>
                  )}
                  {v.estado === "Propuesta" && (
                    <Button
                      size="sm"
                      className="btn-brand rounded-md"
                      data-testid={`visit-confirm-three-way-button-${v.id}`}
                      onClick={() => confirmarTres(v.id)}
                    >
                      Confirmar a los tres
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
